import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosWithoutInterceptor from "../axiosWithoutInterceptor";
import type { DataTransfer, DataTransferListResponse } from "../features/dataTransfer/types";
import toast from "../hooks/toast";
import { redirectToLoginPage } from "../shared/sessionUtility";
import { rowsPerPageDefault } from "../shared/constants";
import { buildApiV3Url } from "../shared/Utils";
import type { AppDispatch, RootState } from "./types";

const [showToast] = toast();

export interface DataTransferState {
    isLoading: boolean;
    items: DataTransfer[];
    total: number;
    page: number;
    pageSize: number;
}

const initialState: DataTransferState = {
    isLoading: false,
    items: [],
    total: 0,
    page: 1,
    // Fallback only — callers seed from session.rowsPerPage on first load.
    pageSize: rowsPerPageDefault,
};

const dataTransferSlice = createSlice({
    name: "dataTransfer",
    initialState,
    reducers: {
        setLoading: (state: DataTransferState, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setTransfersPage: (
            state: DataTransferState,
            action: PayloadAction<{
                page: number;
                pageSize: number;
                response: DataTransferListResponse;
            }>
        ) => {
            state.items = action.payload.response?.value ?? [];
            state.total = action.payload.response?.count ?? 0;
            state.page = action.payload.page;
            state.pageSize = action.payload.pageSize;
        },
    },
});

export const { setLoading, setTransfersPage } = dataTransferSlice.actions;

export const selectDataTransfer = (state: RootState): DataTransferState => state.dataTransfer;

interface ApiError {
    response?: { status?: number; data?: { title?: string } };
}

/**
 * Load a page of transfer history. Pass no arguments to refresh the page currently shown — e.g.
 * the export/import flow dispatches `loadDataTransfers()` when its job completes so the grid picks
 * up the latest status.
 */
export const loadDataTransfers =
    (page?: number, pageSize?: number) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        const current = selectDataTransfer(getState());
        if (current.isLoading) {
            return;
        }
        const nextPage = page ?? current.page;
        const nextPageSize = pageSize ?? current.pageSize;
        dispatch(setLoading(true));
        try {
            const response = await axiosWithoutInterceptor.get<DataTransferListResponse>(
                buildApiV3Url(`data-transfers?page=${nextPage}&pageSize=${nextPageSize}`),
                { withCredentials: true }
            );
            dispatch(
                setTransfersPage({
                    page: nextPage,
                    pageSize: nextPageSize,
                    response: response.data,
                })
            );
        } catch (error) {
            if ((error as ApiError)?.response?.status === 401) {
                redirectToLoginPage();
            } else {
                showToast(
                    "error",
                    (error as ApiError)?.response?.data?.title || "Failed to read transfer history."
                );
            }
        } finally {
            dispatch(setLoading(false));
        }
    };

export default dataTransferSlice.reducer;
